import { Box, Button, Chip, Stack, Typography, useTheme } from '@mui/material';
import { FC, useState } from 'react';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { graphql } from '../gql';
import { Clear } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ItemMultiSelect } from './ItemMultiSelect';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { useSnackbar } from '../store/snackbar';
import { getNightOrDayPalette } from '../utils/getNightOrDayPalette';

const listTagsQuery = graphql(`
	query tagsSelect_listTags {
		listTag {
			id
			name
		}
	}
`);

const createTagMutation = graphql(`
	mutation tagsSelect_createTag($name: String!) {
		createTag(data: { name: $name }) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

type TagsSelectProps = Readonly<{
	value: readonly string[];
	onChange(newTags: readonly string[]): void;
	loading?: boolean;
	hideAdd?: boolean;
	size: 'medium' | 'small';
	showClear?: boolean;
}>;

export const TagsSelect: FC<TagsSelectProps> = ({ value, onChange, loading, hideAdd, size, showClear }) => {
	const { data: tagsCollection, isLoading: isTagsCollectionLoading } = useApiQuery(listTagsQuery);
	const { mutateAsync: createTagAsync } = useApiMutation(createTagMutation);
	const theme = useTheme();
	const intl = useIntl();
	const [isHover, setIsHover] = useState(false);
	const { openSnackbar } = useSnackbar();

	const options =
		tagsCollection?.listTag.map((tag) => ({
			id: tag.id,
			label: tag.name,
		})) ?? [];

	const tags = value.filter((id) => options.some((o) => o.id === id)).map((id) => options.find((o) => o.id === id)!);

	const deleteTag = (tagId: string) => {
		const newTags = value.filter((id) => id !== tagId);
		onChange(newTags);
	};

	const handleTagCreate = (name: string, onSuccess: (id: string) => void) => {
		createTagAsync(
			{ name },
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.createTag.ok,
						toastMessages: {
							error: intl.formatMessage({ id: 'form.tags-select.create.error' }),
						},
						errorMessage: result.createTag.errorMessage,
						openSnackbar,
					});
				},
			},
		).then((result) => {
			if (result.createTag.node?.id != null) {
				onSuccess(result.createTag.node.id);
			}
		});
	};

	if (loading) {
		return (
			<Typography sx={{ color: theme.palette.secondary.main }}>
				<FormattedMessage id={'common.loading'} />
			</Typography>
		);
	}

	return (
		<Stack direction={'column'} gap={2} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
			<Box sx={{ display: tags.length > 0 ? 'flex' : 'none', flexWrap: 'wrap', gap: 1 }}>
				{tags.map(({ id, label }) => (
					<Chip
						key={id}
						label={label}
						size={size}
						sx={{
							'.MuiChip-label': {
								p: 1,
								pl: 3,
							},
							':hover > .MuiSvgIcon-root': {
								opacity: 1,
							},
						}}
						onDelete={() => deleteTag(id)}
						onMouseDown={(event) => {
							event.stopPropagation();
						}}
						deleteIcon={
							<Clear
								sx={{
									fontSize: 16,
									color: (theme) => theme.palette[getNightOrDayPalette(theme)].A50,
									marginLeft: 0.25,
									opacity: 0,
									transition: 'opacity 0.2s',
								}}
							/>
						}
					/>
				))}
			</Box>
			<Stack direction={'row'} sx={{ opacity: hideAdd ? 0 : 1 }} justifyContent={'space-between'} alignItems={'center'}>
				<Stack direction={'column'} flexGrow={showClear ? undefined : 1}>
					<ItemMultiSelect
						label={intl.formatMessage({ id: 'form.tags-select.label' })}
						noItemsText={intl.formatMessage({ id: 'form.tags-select.no-items' })}
						createText={intl.formatMessage({ id: 'form.tags-select.create' })}
						emptyItemErrorText={intl.formatMessage({ id: 'form.tags-select.errors.empty' })}
						createDialogTitle={intl.formatMessage({ id: 'form.tags-select.title.create' })}
						isLoading={isTagsCollectionLoading}
						onChange={onChange}
						defaultValue={value}
						options={options}
						onCreate={handleTagCreate}
					/>
				</Stack>
				{showClear && tags.length >= 2 && (
					<Button
						type={'button'}
						variant={'text'}
						color={'secondary'}
						onClick={() => onChange([])}
						sx={{ opacity: isHover ? 1 : 0, '&:hover': { background: 'transparent' } }}
					>
						<Typography variant={'h6'} color={theme.palette.grey[700]} sx={{ textDecoration: 'underline' }}>
							<FormattedMessage id={'form.tags-select.clear'} />
						</Typography>
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
